import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, ImagePlus, Images, Loader2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

const cache = {};
export const Vignette = ({ id, className = '' }) => {
  const [url, setUrl] = useState(cache[id]);
  useEffect(() => {
    if (cache[id]) return setUrl(cache[id]);
    axios.get(`/livret-base/photos/${id}`, { responseType: 'blob' })
      .then((r) => { cache[id] = URL.createObjectURL(r.data); setUrl(cache[id]); }).catch(() => {});
  }, [id]);
  return url ? <img src={url} alt="" className={`object-cover ${className}`} /> : <div className={`animate-pulse bg-slate-200 ${className}`} />;
};

const Envoi = ({ onAjout }) => {
  const input = useRef(null);
  const [envoi, setEnvoi] = useState(false);
  const televerser = async (e) => {
    const fichiers = [...(e.target.files || [])];
    e.target.value = '';
    setEnvoi(true);
    for (const f of fichiers) {
      const fd = new FormData();
      fd.append('file', f);
      try { onAjout((await axios.post('/livret-base/photos', fd)).data); }
      catch (err) { toast.error(err.response?.data?.detail || `Échec de l'ajout de ${f.name}`); }
    }
    setEnvoi(false);
  };
  return (
    <>
      <input ref={input} type="file" accept="image/*" multiple className="hidden" onChange={televerser} data-testid="livret-base-photo-input" />
      <Button type="button" variant="outline" disabled={envoi} onClick={() => input.current?.click()} data-testid="livret-base-photo-ajouter">
        {envoi ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />} Ajouter des photos
      </Button>
    </>
  );
};

export const ChoixPhotosDialog = ({ open, titre, max, choisies, onValider, onClose }) => {
  const [biblio, setBiblio] = useState([]);
  const [sel, setSel] = useState([]);
  useEffect(() => {
    if (!open) return;
    setSel(choisies || []);
    axios.get('/livret-base/photos').then(({ data }) => setBiblio(data)).catch(() => setBiblio([]));
  }, [open, choisies]);

  const basculer = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : sel.length >= max ? (max === 1 ? [id] : sel) : [...sel, id]);
  const supprimer = async (id) => {
    if (!window.confirm('Supprimer définitivement cette photo de votre bibliothèque ?')) return;
    await axios.delete(`/livret-base/photos/${id}`);
    setBiblio(biblio.filter((p) => p.photo_id !== id));
    setSel(sel.filter((x) => x !== id));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="livret-base-photos-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Images className="h-5 w-5" /> Photos : {titre}</DialogTitle>
          <DialogDescription>Choisissez jusqu'à {max} photo{max > 1 ? 's' : ''} dans l'ordre d'affichage ({sel.length}/{max}).</DialogDescription>
        </DialogHeader>
        <Envoi onAjout={(p) => { setBiblio((b) => [p, ...b]); setSel((s) => (s.length < max ? [...s, p.photo_id] : max === 1 ? [p.photo_id] : s)); }} />
        {biblio.length === 0 && <p className="text-sm text-slate-400" data-testid="livret-base-photos-vide">Aucune photo pour l'instant : ajoutez vos photos de la base.</p>}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {biblio.map((p) => {
            const rang = sel.indexOf(p.photo_id);
            return (
              <div key={p.photo_id} className={`group relative overflow-hidden rounded-lg ring-2 transition-shadow ${rang >= 0 ? 'ring-[#8DC63F]' : 'ring-transparent'}`}>
                <button type="button" onClick={() => basculer(p.photo_id)} className="block w-full" data-testid={`livret-base-photo-${p.photo_id}`}>
                  <Vignette id={p.photo_id} className="h-28 w-full" />
                </button>
                {rang >= 0 && <span className="absolute left-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#8DC63F] text-xs font-bold text-white">{max === 1 ? <Check className="h-4 w-4" /> : rang + 1}</span>}
                <button type="button" onClick={() => supprimer(p.photo_id)} title="Supprimer" data-testid={`livret-base-photo-suppr-${p.photo_id}`}
                  className="absolute right-1.5 top-1.5 rounded-full bg-white/90 p-1 text-red-600 opacity-0 transition-opacity group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            );
          })}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onValider(null)} data-testid="livret-base-photos-defaut">Photos d'origine</Button>
          <Button variant="outline" onClick={() => onValider([])} data-testid="livret-base-photos-aucune">Aucune photo</Button>
          <Button onClick={() => onValider(sel)} disabled={!sel.length} className="bg-[#0F172A] text-white" data-testid="livret-base-photos-valider">Valider</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
